// src/pages/TokenList.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  AdjustmentsHorizontalIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/LoadingSpinner';
import TokenCard from '../components/TokenCard';

const TokenList = () => {
  const [tokens, setTokens] = useState([]);
  const [filteredTokens, setFilteredTokens] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pairFilter, setPairFilter] = useState('all');
  const [pairTypes, setPairTypes] = useState(['WOAS', 'MCHC', 'MSM', 'Z']);
  const [showFilters, setShowFilters] = useState(true);

  // トークン一覧を取得
  useEffect(() => {
    const fetchTokens = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/tokens`);
        // grantTokenがNULLでないトークンのみをフィルタリング
        const validTokens = response.data.filter(token => token.grantToken);
        setTokens(validTokens);
        setFilteredTokens(validTokens);

        // Get active token pairs from ContractAddress
        try {
          const contractResponse = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/contracts/active`
          );

          // デフォルトのペア（常に表示）
          const defaultPairs = ['WOAS', 'MCHC', 'MSM', 'Z'];

          // バックエンドからのアクティブなペアを取得
          const activePairs = contractResponse.data
            .sort((a, b) => a.id - b.id)
            .map(pair => pair.name);

          // 必要なペアがすべて含まれるようにする
          const missingPairs = defaultPairs.filter(pair => !activePairs.includes(pair));

          // 必要なペアをすべて含む最終リスト
          const allPairs = [...activePairs];

          // 不足しているペアを追加
          missingPairs.forEach(pair => {
            if (!allPairs.includes(pair)) {
              allPairs.push(pair);
            }
          });

          setPairTypes(allPairs);
        } catch (pairError) {
          console.error('トークンペア取得エラー:', pairError);
          // エラー時はデフォルトのペアを使用
          setPairTypes(['WOAS', 'MCHC', 'MSM', 'Z']);
        }

        setIsLoading(false);
      } catch (error) {
        console.error('コイン取得エラー:', error);
        setError('コイン一覧の取得に失敗しました');
        setIsLoading(false);
      }
    };

    fetchTokens();
  }, []);

  // 検索フィルタとペアタイプフィルタの適用
  useEffect(() => {
    let result = [...tokens];

    // 検索クエリでフィルタリング
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      result = result.filter(token => {
        const name = token.name?.toLowerCase() || '';
        const symbol = token.symbol?.toLowerCase() || '';
        const grantToken = token.grantToken?.toLowerCase() || '';
        const pairType = token.pairType?.toLowerCase() || '';

        return (
          name.includes(query) ||
          symbol.includes(query) ||
          grantToken.includes(query) ||
          pairType.includes(query)
        );
      });
    }

    // ペアタイプでフィルタリング
    if (pairFilter !== 'all') {
      result = result.filter(token => token.pairType === pairFilter);
    }

    setFilteredTokens(result);
  }, [tokens, searchQuery, pairFilter]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">コイン一覧</h1>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* 検索ボックスとフィルター */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="コイン名、シンボル、アドレスで検索..."
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="ml-2 p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 border border-gray-300 dark:border-gray-600 rounded"
          >
            <AdjustmentsHorizontalIcon className="h-5 w-5" />
          </button>
        </div>

        {/* フィルターオプション */}
        {showFilters && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            {/* ペアタイプフィルター */}
            <div>
              <div className="flex items-center mb-2">
                <TagIcon className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-1" />
                <span className="text-sm text-gray-500 dark:text-gray-400">トークンペア:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setPairFilter('all')}
                  className={`px-3 py-1.5 text-sm rounded-full ${
                    pairFilter === 'all'
                      ? 'bg-blue-600 dark:bg-blue-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  すべて
                </button>

                {/* デフォルトの4ペアタイプを常に表示 */}
                {pairTypes.map(pairType => (
                  <button
                    key={pairType}
                    onClick={() => setPairFilter(pairType)}
                    className={`px-3 py-1.5 text-sm rounded-full ${
                      pairFilter === pairType
                        ? 'bg-blue-600 dark:bg-blue-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {pairType}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          {filteredTokens.length} 件のトークンが見つかりました
          {(searchQuery || pairFilter !== 'all') && <span> (フィルター適用中)</span>}
        </div>
      </div>

      {/* トークンリスト */}
      {filteredTokens.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTokens.map(token => (
            <TokenCard key={token.id} token={token} />
          ))}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <div className="text-gray-500 dark:text-gray-400 mb-4">
            トークンが見つかりませんでした
          </div>
          {(searchQuery || pairFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setPairFilter('all');
              }}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              フィルターをクリア
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TokenList;
